import { EIPD_CONFIG, NA_VALUE } from './constants'

/**
 * Determines if EIPD (DPIA - Data Protection Impact Assessment) is required
 * Nuanced rule from Plantilla ARI+RAT addval v1.1:
 *
 * EIPD required if:
 *   (a) Any of the 4 legal obligation checkboxes (OL-a/b/c/d) = true
 *   OR
 *   (b) Nuanced rule:
 *       - At least 1 EIPD factor with score ≥ 3
 *       AND one of:
 *         - flags.hasSensitive = true
 *         - flags.hasMinors = true
 *         - flags.hasBiometric = true
 *         - EIPD average score ≥ threshold
 *
 * @param {Array<number|null>} scores - EIPD factor scores (1-5 or null for N/A)
 * @param {Object} flags - Data processing flags
 * @param {boolean} flags.hasSensitive - Has sensitive data
 * @param {boolean} flags.hasMinors - Has minors' data
 * @param {boolean} flags.hasBiometric - Has biometric data
 * @param {Object} legalObligations - Legal obligation flags
 * @param {boolean} legalObligations.ol_a - Legal obligation A
 * @param {boolean} legalObligations.ol_b - Legal obligation B
 * @param {boolean} legalObligations.ol_c - Legal obligation C
 * @param {boolean} legalObligations.ol_d - Legal obligation D
 * @returns {boolean} True if EIPD is required
 */
export function shouldRequireEipd(scores, flags = {}, legalObligations = {}) {
  // (a) Check legal obligations - if any is true, EIPD is required
  if (legalObligations.ol_a || legalObligations.ol_b || legalObligations.ol_c || legalObligations.ol_d) {
    return true
  }

  // (b) Check nuanced rule
  if (!scores || !Array.isArray(scores)) {
    return false
  }

  // Filter valid scores
  const validScores = scores.filter(score => score !== NA_VALUE && !isNaN(score))

  if (validScores.length === 0) {
    return false
  }

  // Check if at least one factor ≥ MIN_FACTOR_SCORE
  const hasHighFactor = validScores.some(score => score >= EIPD_CONFIG.MIN_FACTOR_SCORE)

  if (!hasHighFactor) {
    return false
  }

  // Check if any special data type flag is true
  const hasSpecialDataType = flags.hasSensitive === true || flags.hasMinors === true || flags.hasBiometric === true

  if (hasSpecialDataType) {
    return true
  }

  // Check average score threshold
  const avgScore = validScores.reduce((sum, score) => sum + score, 0) / validScores.length

  if (avgScore >= EIPD_CONFIG.MIN_AVG_SCORE) {
    return true
  }

  return false
}
